import type { UnclassifiedAlert } from "@sitescene/types";

import { useNavigate } from "react-router";

import { type Column, DataTable } from "@/components/data-table";
import { Button } from "@/components/field";
import { StatusMessage } from "@/components/status-message";
import { plausibilityVerificationPath, ROUTES } from "@/constants/routes";
import { useOrgSelection } from "@/context/use-org-selection";
import {
	useIgnoreUnclassified,
	useUnclassifiedAlerts,
} from "@/features/plausibility/use-unclassified";

import { formatWhen } from "./format";

/**
 * Alerts no alertable class explained, across every organization. The
 * verifier still checked each one's footage against Stage 1's own
 * description; this is where staff see what the taxonomy is missing and
 * create the class the alert needed.
 */
export const UnclassifiedAlerts = () => {
	const alerts = useUnclassifiedAlerts("pending");
	const ignore = useIgnoreUnclassified();
	const { organizations, selectOrg } = useOrgSelection();
	const navigate = useNavigate();
	const rows = alerts.data ?? [];

	const organizationOf = (row: UnclassifiedAlert) =>
		organizations.find((org) => org.tenantId === row.tenantId);

	const columns: Column<UnclassifiedAlert>[] = [
		{
			header: "When",
			key: "when",
			render: (row) => (
				<span className="text-secondary text-xs">
					{formatWhen(row.capturedAt)}
				</span>
			),
		},
		{
			header: "Organization",
			key: "org",
			render: (row) => organizationOf(row)?.name ?? row.tenantId,
		},
		{
			header: "Stage 1",
			key: "stage1",
			render: (row) => (
				<div className="max-w-xl">
					<div className="text-primary text-xs">
						<code>{row.type}</code> · severity {row.severity}
						{row.labels.length > 0 && ` · ${row.labels.join(", ")}`}
					</div>
					{row.description && (
						<div className="text-secondary text-xs">{row.description}</div>
					)}
				</div>
			),
		},
		{
			header: "Footage check",
			key: "decision",
			render: (row) => (
				<span className="text-secondary text-xs">{row.decision ?? "—"}</span>
			),
		},
		{
			header: "",
			key: "actions",
			render: (row) => {
				const org = organizationOf(row);
				return (
					<div className="flex flex-wrap gap-2">
						<Button
							disabled={!org}
							onClick={() => {
								if (!org) return;
								selectOrg(org.orgCode);
								void navigate(plausibilityVerificationPath(row.verificationId));
							}}
							title={
								org ? undefined : "You have no access to this organization"
							}
							type="button"
							variant="secondary"
						>
							Open verification
						</Button>
						<Button
							onClick={() =>
								void navigate(
									`${ROUTES.GLOBAL_TAXONOMY}?${new URLSearchParams({
										description: row.description ?? "",
										from: row.id,
										name: humanize(row.type),
										new: "1",
									}).toString()}`,
								)
							}
							type="button"
						>
							Create class
						</Button>
						<Button
							disabled={ignore.isPending}
							onClick={() => ignore.mutate([row.id])}
							type="button"
							variant="danger"
						>
							Ignore
						</Button>
					</div>
				);
			},
		},
	];

	return (
		<div>
			<h2 className="text-primary text-base font-semibold">
				Unclassified alerts
			</h2>
			<p className="text-secondary text-sm">
				Alerts no alertable class explained. Their footage was still checked,
				against Stage 1's own description. Create the class an alert needed, or
				ignore it.
			</p>
			{ignore.error && (
				<p className="text-error-primary mt-2 text-sm" role="alert">
					{ignore.error.message}
				</p>
			)}
			<div className="mt-4">
				<StatusMessage
					empty="No unclassified alerts. Every recent alert matched a class."
					error={alerts.error}
					isEmpty={!alerts.isLoading && rows.length === 0}
					isLoading={alerts.isLoading}
					loading="Loading unclassified alerts…"
				/>
				{rows.length > 0 && (
					<DataTable columns={columns} rowKey={(row) => row.id} rows={rows} />
				)}
			</div>
		</div>
	);
};

/** `construction_work` → `Construction work`, as a starting display name. */
function humanize(type: string): string {
	const words = type.replace(/[_-]+/g, " ").trim();
	return words ? words[0]!.toUpperCase() + words.slice(1) : "";
}
